import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { db } from "../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaMoneyBillWave,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaSyncAlt,
  FaChevronRight,
  FaUser,
} from "react-icons/fa";

import "./NewWithdrawals.css";


/* =========================================================
   SETTINGS
========================================================= */

const CACHE_TIME =
  2 * 60 * 1000;

const MAX_LEVEL = 3;

const MAX_BATCH = 10;

const WITHDRAW_COLLECTION =
  "withdrawals";


/* =========================================================
   CACHE
========================================================= */

const getCacheKey = (
  username
) =>
  `newWithdrawCache_${username}`;


/* =========================================================
   CHUNK
========================================================= */

const chunkArray = (
  array,
  size = MAX_BATCH
) => {

  const chunks = [];

  for (
    let i = 0;
    i < array.length;
    i += size
  ) {

    chunks.push(
      array.slice(
        i,
        i + size
      )
    );

  }

  return chunks;
};


/* =========================================================
   DATE
========================================================= */

const getDateValue = (
  value
) => {

  if (!value) {
    return null;
  }

  try {

    if (
      value?.toDate
    ) {

      return value.toDate();

    }


    if (
      value?.seconds
    ) {

      return new Date(
        value.seconds * 1000
      );

    }


    if (
      value instanceof Date
    ) {

      return value;

    }


    const date =
      new Date(value);


    if (
      Number.isNaN(
        date.getTime()
      )
    ) {

      return null;

    }


    return date;

  } catch {

    return null;

  }

};


/* =========================================================
   TODAY
========================================================= */

const isToday = (
  value
) => {

  const date =
    getDateValue(value);

  if (!date) {
    return false;
  }


  const today =
    new Date();


  return (
    date.getFullYear() ===
      today.getFullYear() &&

    date.getMonth() ===
      today.getMonth() &&

    date.getDate() ===
      today.getDate()
  );

};


/* =========================================================
   TIME
========================================================= */

const formatTime = (
  value
) => {

  const date =
    getDateValue(value);


  if (!date) {
    return "--:--";
  }


  return date.toLocaleTimeString(
    [],
    {
      hour: "2-digit",
      minute: "2-digit",
    }
  );

};


/* =========================================================
   AMOUNT
========================================================= */

const formatAmount = (
  amount
) => {

  const number =
    Number(
      amount || 0
    );


  if (
    !Number.isFinite(
      number
    )
  ) {

    return "0";

  }


  return number.toLocaleString();

};


/* =========================================================
   STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  if (!status) {
    return "pending";
  }


  const value =
    String(status)
      .trim()
      .toLowerCase();


  if (
    [
      "success",
      "successful",
      "completed",
      "complete",
      "paid",
      "approved",
    ].includes(value)
  ) {

    return "success";

  }


  if (
    [
      "failed",
      "failure",
      "cancelled",
      "canceled",
      "rejected",
      "declined",
    ].includes(value)
  ) {

    return "failed";

  }


  return "pending";

};


/* =========================================================
   USERNAME
========================================================= */

const getUsername = (
  data
) => {

  return (
    data?.username ||
    data?.userName ||
    data?.user ||
    data?.name ||
    ""
  );

};


/* =========================================================
   WITHDRAW AMOUNT
========================================================= */

const getWithdrawAmount = (
  data
) => {

  return (
    data?.amount ??
    data?.withdrawAmount ??
    data?.withdrawalAmount ??
    data?.transactionAmount ??
    data?.paymentAmount ??
    data?.value ??
    0
  );

};


/* =========================================================
   CURRENCY
========================================================= */

const getCurrency = (
  data
) => {

  if (
    data?.currency
  ) {

    return String(
      data.currency
    ).toUpperCase();

  }


  if (
    data?.currencyCode
  ) {

    return String(
      data.currencyCode
    ).toUpperCase();

  }


  if (
    data?.coin
  ) {

    return String(
      data.coin
    ).toUpperCase();

  }


  if (
    data?.method
  ) {

    const method =
      String(
        data.method
      ).toLowerCase();


    if (
      method.includes(
        "usdt"
      )
    ) {

      return "USDT";

    }

  }


  return "TZS";

};


/* =========================================================
   CREATED DATE
========================================================= */

const getCreatedAt = (
  data
) => {

  return (
    data?.createdAt ||
    data?.timestamp ||
    data?.date ||
    data?.withdrawDate ||
    data?.transactionDate ||
    data?.created ||
    data?.time ||
    null
  );

};


/* =========================================================
   STATUS ICON
========================================================= */

const StatusIcon = ({
  status,
}) => {

  if (
    status ===
    "success"
  ) {

    return (
      <FaCheckCircle />
    );

  }


  if (
    status ===
    "failed"
  ) {

    return (
      <FaTimesCircle />
    );

  }


  return (
    <FaHourglassHalf />
  );

};


/* =========================================================
   NORMALIZE WITHDRAW
========================================================= */

const normalizeWithdraw = (
  doc
) => {

  const data =
    doc.data();


  const createdAt =
    getCreatedAt(data);


  /*
   * Only today's withdrawals
   */

  if (
    !isToday(
      createdAt
    )
  ) {

    return null;

  }


  return {

    id:
      `withdrawal_${doc.id}`,

    originalId:
      doc.id,

    username:
      getUsername(data),

    amount:
      Number(
        getWithdrawAmount(
          data
        ) || 0
      ),

    currency:
      getCurrency(
        data
      ),

    status:
      normalizeStatus(
        data.status
      ),

    createdAt,

    raw:
      data,

  };

};


/* =========================================================
   NEW WITHDRAW
========================================================= */

const NewWithdraw = () => {

  const navigate =
    useNavigate();


  const [
    levels,
    setLevels,
  ] = useState({
    1: [],
    2: [],
    3: [],
  });


  const [
    activeLevel,
    setActiveLevel,
  ] = useState(1);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    refreshing,
    setRefreshing,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  const loggedInUsername =
    localStorage.getItem(
      "username"
    );


  /* =======================================================
     SAVE CACHE
  ======================================================= */

  const saveCache =
    useCallback(
      (data) => {

        if (
          !loggedInUsername
        ) {

          return;

        }


        try {

          localStorage.setItem(

            getCacheKey(
              loggedInUsername
            ),

            JSON.stringify({

              data,

              updatedAt:
                Date.now(),

            })

          );

        } catch (
          error
        ) {

          console.error(
            "Withdraw cache error:",
            error
          );

        }

      },
      [
        loggedInUsername,
      ]
    );


  /* =======================================================
     LOAD CACHE
  ======================================================= */

  const loadCache =
    useCallback(
      () => {

        if (
          !loggedInUsername
        ) {

          return null;

        }


        try {

          const raw =
            localStorage.getItem(
              getCacheKey(
                loggedInUsername
              )
            );


          if (!raw) {
            return null;
          }


          const cached =
            JSON.parse(
              raw
            );


          if (
            !cached?.data
          ) {

            return null;

          }


          return cached;

        } catch {

          return null;

        }

      },
      [
        loggedInUsername,
      ]
    );


  /* =======================================================
     CURRENT USER
  ======================================================= */

  const fetchCurrentUser =
    useCallback(
      async () => {

        if (
          !loggedInUsername
        ) {

          return null;

        }


        const q =
          query(
            collection(
              db,
              "users"
            ),

            where(
              "username",
              "==",
              loggedInUsername
            ),

            limit(1)
          );


        const snap =
          await getDocs(
            q
          );


        if (
          snap.empty
        ) {

          return null;

        }


        const doc =
          snap.docs[0];


        return {

          id:
            doc.id,

          ...doc.data(),

        };

      },
      [
        loggedInUsername,
      ]
    );


  /* =======================================================
     FETCH USERS BY REFERRAL CODES
  ======================================================= */

  const fetchUsersByCodes =
    async (
      referralCodes
    ) => {

      if (
        !referralCodes?.length
      ) {

        return [];

      }


      const uniqueCodes =
        [
          ...new Set(
            referralCodes
              .filter(Boolean)
          ),
        ];


      const chunks =
        chunkArray(
          uniqueCodes
        );


      const snapshots =
        await Promise.all(

          chunks.map(
            async (
              chunk
            ) => {

              try {

                const q =
                  query(
                    collection(
                      db,
                      "users"
                    ),

                    where(
                      "whoReferredWho",
                      "in",
                      chunk
                    )
                  );


                return await getDocs(
                  q
                );

              } catch (
                error
              ) {

                console.error(
                  "User batch error:",
                  error
                );

                return null;

              }

            }
          )

        );


      const users = [];


      snapshots.forEach(
        (
          snap
        ) => {

          if (!snap) {
            return;
          }


          snap.forEach(
            (
              doc
            ) => {

              users.push({

                id:
                  doc.id,

                ...doc.data(),

              });

            }
          );

        }
      );


      /*
       * Remove duplicates
       */

      return Array.from(

        new Map(

          users.map(
            (
              user
            ) => [
              user.id,
              user,
            ]
          )

        ).values()

      );

    };


  /* =======================================================
     FETCH WITHDRAWALS FOR MANY USERS
  ======================================================= */

  const fetchWithdrawalsForUsers =
    async (
      usernames
    ) => {

      const uniqueUsernames =
        [
          ...new Set(
            usernames
              .filter(Boolean)
          ),
        ];


      const withdrawalMap =
        new Map();


      if (
        !uniqueUsernames.length
      ) {

        return withdrawalMap;

      }


      uniqueUsernames.forEach(
        (
          username
        ) => {

          withdrawalMap.set(
            username,
            []
          );

        }
      );


      /*
       * Firestore IN supports
       * max 10 values.
       */

      const chunks =
        chunkArray(
          uniqueUsernames
        );


      await Promise.all(

        chunks.map(
          async (
            usernameChunk
          ) => {

            try {

              const q =
                query(

                  collection(
                    db,
                    WITHDRAW_COLLECTION
                  ),

                  where(
                    "username",
                    "in",
                    usernameChunk
                  )

                );


              const snap =
                await getDocs(
                  q
                );


              snap.forEach(
                (
                  doc
                ) => {

                  const withdrawal =
                    normalizeWithdraw(
                      doc
                    );


                  if (
                    !withdrawal
                  ) {

                    return;

                  }


                  const username =
                    withdrawal.username;


                  if (
                    !username
                  ) {

                    return;

                  }


                  if (
                    !withdrawalMap.has(
                      username
                    )
                  ) {

                    withdrawalMap.set(
                      username,
                      []
                    );

                  }


                  withdrawalMap
                    .get(username)
                    .push(
                      withdrawal
                    );

                }
              );


            } catch (
              error
            ) {

              console.error(
                "Withdrawal query error:",
                error
              );

            }

          }
        )

      );


      return withdrawalMap;

    };


  /* =======================================================
     FETCH LEVELS
  ======================================================= */

  const fetchLevelData =
    async (
      referralCode
    ) => {

      const result = {

        1: [],
        2: [],
        3: [],

      };


      let currentCodes = [
        referralCode,
      ];


      for (
        let level = 1;
        level <= MAX_LEVEL;
        level++
      ) {

        if (
          !currentCodes.length
        ) {

          break;

        }


        /*
         * Fetch users.
         */

        const users =
          await fetchUsersByCodes(
            currentCodes
          );


        if (
          !users.length
        ) {

          result[level] = [];

          break;

        }


        /*
         * Usernames.
         */

        const usernames =
          users
            .map(
              (
                user
              ) =>
                user.username
            )
            .filter(Boolean);


        /*
         * Fetch withdrawals
         * for all users in batch.
         */

        const withdrawalMap =
          await fetchWithdrawalsForUsers(
            usernames
          );


        /*
         * Attach withdrawals.
         */

        const usersWithWithdrawals =
          users
            .map(
              (
                user
              ) => {

                const withdrawals =
                  withdrawalMap.get(
                    user.username
                  ) || [];


                /*
                 * Newest first.
                 */

                withdrawals.sort(
                  (
                    a,
                    b
                  ) => {

                    const dateA =
                      getDateValue(
                        a.createdAt
                      );


                    const dateB =
                      getDateValue(
                        b.createdAt
                      );


                    return (

                      (
                        dateB?.getTime() ||
                        0
                      )

                      -

                      (
                        dateA?.getTime() ||
                        0
                      )

                    );

                  }
                );


                /*
                 * Successful total.
                 */

                const successful =
                  withdrawals.filter(
                    (
                      withdrawal
                    ) =>
                      withdrawal.status ===
                      "success"
                  );


                const withdrawalTotal =
                  successful.reduce(
                    (
                      total,
                      withdrawal
                    ) =>
                      total +
                      Number(
                        withdrawal.amount ||
                          0
                      ),
                    0
                  );


                return {

                  ...user,

                  username:
                    user.username ||
                    "Unknown",

                  withdrawals,

                  withdrawalTotal,

                };

              }
            )
            /*
             * Only members who
             * withdrew today.
             */

            .filter(
              (
                user
              ) =>
                user.withdrawals
                  .length > 0
            );


        result[level] =
          usersWithWithdrawals;


        /*
         * Prepare next level.
         */

        currentCodes =
          users
            .map(
              (
                user
              ) =>
                user.referralCode
            )
            .filter(Boolean);

      }


      return result;

    };


  /* =======================================================
     LOAD WITHDRAWALS
  ======================================================= */

  const loadWithdrawals =
    useCallback(
      async (
        showRefresh = false
      ) => {

        if (
          !loggedInUsername
        ) {

          setLoading(false);

          return;

        }


        if (
          showRefresh
        ) {

          setRefreshing(true);

        }


        setError("");


        try {

          const user =
            await fetchCurrentUser();


          if (
            !user?.referralCode
          ) {

            const empty = {

              1: [],
              2: [],
              3: [],

            };


            setLevels(
              empty
            );


            saveCache(
              empty
            );


            return;

          }


          const data =
            await fetchLevelData(
              user.referralCode
            );


          setLevels(
            data
          );


          saveCache(
            data
          );


        } catch (
          error
        ) {

          console.error(
            "New withdrawal error:",
            error
          );


          setError(
            "Failed to load new withdrawals."
          );

        } finally {

          setLoading(
            false
          );

          setRefreshing(
            false
          );

        }

      },
      [
        loggedInUsername,
        fetchCurrentUser,
        saveCache,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(
    () => {

      if (
        !loggedInUsername
      ) {

        setLoading(false);

        return;

      }


      /*
       * CACHE FIRST
       */

      const cached =
        loadCache();


      if (
        cached?.data
      ) {

        setLevels({

          1:
            cached.data[1] ||
            [],

          2:
            cached.data[2] ||
            [],

          3:
            cached.data[3] ||
            [],

        });


        /*
         * Show immediately.
         */

        setLoading(
          false
        );

      }


      /*
       * Background update.
       */

      loadWithdrawals(
        false
      );

    },
    [
      loggedInUsername,
      loadCache,
      loadWithdrawals,
    ]
  );


  /* =======================================================
     AUTO REFRESH
  ======================================================= */

  useEffect(
    () => {

      if (
        !loggedInUsername
      ) {

        return;

      }


      const interval =
        setInterval(
          () => {

            loadWithdrawals(
              false
            );

          },
          CACHE_TIME
        );


      return () => {

        clearInterval(
          interval
        );

      };

    },
    [
      loggedInUsername,
      loadWithdrawals,
    ]
  );


  /* =======================================================
     CALCULATIONS
  ======================================================= */

  const currentUsers =
    levels[
      activeLevel
    ] || [];


  const allWithdrawals =
    currentUsers.flatMap(
      (
        user
      ) =>
        user.withdrawals ||
        []
    );


  const successfulWithdrawals =
    allWithdrawals.filter(
      (
        withdrawal
      ) =>
        withdrawal.status ===
        "success"
    );


  const totalWithdraw =
    successfulWithdrawals.reduce(
      (
        total,
        withdrawal
      ) =>
        total +
        Number(
          withdrawal.amount ||
            0
        ),
      0
    );


  const totalWithdrawals =
    allWithdrawals.length;


  const totalMembers =
    Object.values(
      levels
    ).reduce(
      (
        total,
        users
      ) =>
        total +
        users.length,
      0
    );


  /* =======================================================
     LOGIN
  ======================================================= */

  if (
    !loggedInUsername
  ) {

    return (

      <div className="withdraw-wrapper">

        <div className="withdraw-empty">

          <div className="withdraw-empty-icon">

            <FaUser />

          </div>


          <h3>
            Login Required
          </h3>


          <p>
            Please login first to view
            your team's new withdrawals.
          </p>


          <button
            type="button"
            className="withdraw-back-empty"
            onClick={() =>
              navigate(-1)
            }
          >

            <FaArrowLeft />

            Back

          </button>

        </div>

      </div>

    );

  }


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="withdraw-wrapper">

        <div className="withdraw-loading">

          <div className="withdraw-spinner" />


          <strong>
            Loading new withdrawals
          </strong>


          <span>
            Checking today's withdrawals...
          </span>

        </div>

      </div>

    );

  }


  /* =======================================================
     MAIN
  ======================================================= */

  return (

    <div className="withdraw-wrapper">


      {/* =================================================
          HEADER
      ================================================= */}

      <div className="withdraw-header">

        <div className="withdraw-header-left">


          <button
            type="button"
            className="withdraw-back-btn"
            onClick={() =>
              navigate(-1)
            }
          >

            <FaArrowLeft />

            <span>
              Back
            </span>

          </button>


          <div className="withdraw-title">


            <div className="withdraw-title-icon">

              <FaMoneyBillWave />

            </div>


            <div>

              <span>
                TEAM ACTIVITY
              </span>


              <h1>
                New Withdraw
              </h1>

            </div>


          </div>


        </div>


        <button
          type="button"
          className={
            refreshing
              ? "withdraw-refresh spinning"
              : "withdraw-refresh"
          }
          onClick={() =>
            loadWithdrawals(
              true
            )
          }
          title="Refresh withdrawals"
        >

          <FaSyncAlt />

        </button>


      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div className="withdraw-error">

          <span>
            {error}
          </span>


          <button
            type="button"
            onClick={() =>
              loadWithdrawals(
                true
              )
            }
          >

            Retry

          </button>

        </div>

      )}


      {/* =================================================
          SUMMARY
      ================================================= */}

      <div className="withdraw-summary">


        <div className="withdraw-summary-icon">

          <FaMoneyBillWave />

        </div>


        <div className="withdraw-summary-main">

          <span>
            TODAY'S SUCCESSFUL WITHDRAWALS
          </span>


          <strong>
            {formatAmount(
              totalWithdraw
            )}
          </strong>


          <small>
            Across your team
          </small>

        </div>


        <div className="withdraw-summary-side">

          <strong>
            {totalWithdrawals}
          </strong>


          <span>
            Withdrawals
          </span>

        </div>


      </div>


      {/* =================================================
          STATS
      ================================================= */}

      <div className="withdraw-stats">


        <div className="withdraw-stat">

          <div className="withdraw-stat-icon">

            <FaUsers />

          </div>


          <div>

            <span>
              MEMBERS
            </span>


            <strong>
              {totalMembers}
            </strong>

          </div>

        </div>


        <div className="withdraw-stat">

          <div className="withdraw-stat-icon">

            <FaCheckCircle />

          </div>


          <div>

            <span>
              SUCCESS
            </span>


            <strong>
              {
                successfulWithdrawals.length
              }
            </strong>

          </div>

        </div>


        <div className="withdraw-stat">

          <div className="withdraw-stat-icon">

            <FaClock />

          </div>


          <div>

            <span>
              LEVEL
            </span>


            <strong>
              {activeLevel}
            </strong>

          </div>

        </div>


      </div>


      {/* =================================================
          LEVEL TABS
      ================================================= */}

      <div className="withdraw-level-tabs">

        {[1, 2, 3].map(
          (
            level
          ) => {

            const count =
              levels[level]
                ?.length || 0;


            return (

              <button
                key={level}
                type="button"
                className={
                  activeLevel === level
                    ? "withdraw-level-tab active"
                    : "withdraw-level-tab"
                }
                onClick={() =>
                  setActiveLevel(
                    level
                  )
                }
              >

                <span>
                  LEVEL {level}
                </span>


                <strong>
                  {count}
                </strong>

              </button>

            );

          }
        )}

      </div>


      {/* =================================================
          SECTION HEADER
      ================================================= */}

      <div className="withdraw-section-head">

        <div>

          <span>
            TODAY'S WITHDRAWALS
          </span>


          <strong>
            Level {activeLevel}
          </strong>

        </div>


        <div className="withdraw-section-count">

          {currentUsers.length}


          <span>
            members
          </span>

        </div>

      </div>


      {/* =================================================
          EMPTY
      ================================================= */}

      {currentUsers.length === 0 ? (

        <div className="withdraw-empty">


          <div className="withdraw-empty-icon">

            <FaMoneyBillWave />

          </div>


          <h3>
            No New Withdrawals
          </h3>


          <p>
            There are no withdrawals from
            Level {activeLevel} today.
          </p>


          <div className="withdraw-empty-date">

            <FaClock />

            <span>
              Checked today
            </span>

          </div>


        </div>

      ) : (


        /* =================================================
           USERS
        ================================================= */

        <div className="withdraw-list">


          {currentUsers.map(
            (
              user,
              index
            ) => {


              const withdrawals =
                user.withdrawals ||
                [];


              return (

                <div
                  key={
                    user.id
                  }
                  className="withdraw-user-card"
                >


                  {/* USER HEADER */}

                  <div className="withdraw-user-header">


                    <div className="withdraw-number">

                      {String(
                        index + 1
                      ).padStart(
                        2,
                        "0"
                      )}

                    </div>


                    <div className="withdraw-avatar">

                      <FaUser />

                    </div>


                    <div className="withdraw-user-info">

                      <strong>

                        {
                          user.username ||
                          "Unknown"
                        }

                      </strong>


                      <span>
                        Level {activeLevel}
                      </span>

                    </div>


                    <div className="withdraw-user-total">

                      <span>
                        TOTAL
                      </span>


                      <strong>

                        {
                          formatAmount(
                            user.withdrawalTotal
                          )
                        }

                      </strong>

                    </div>


                  </div>


                  {/* WITHDRAWALS */}

                  <div className="withdraw-transactions">


                    {withdrawals.map(
                      (
                        withdrawal
                      ) => {


                        const status =
                          withdrawal.status;


                        return (

                          <div
                            key={
                              withdrawal.id
                            }
                            className="withdraw-row"
                          >


                            <div className="withdraw-row-icon">

                              <FaMoneyBillWave />

                            </div>


                            <div className="withdraw-row-info">


                              <strong>

                                {
                                  formatAmount(
                                    withdrawal.amount
                                  )
                                }

                                {" "}

                                {
                                  withdrawal.currency
                                }

                              </strong>


                              <div className="withdraw-row-meta">


                                <span>

                                  <FaClock />

                                  {
                                    formatTime(
                                      withdrawal.createdAt
                                    )
                                  }

                                </span>


                                <span>

                                  {
                                    withdrawal.currency
                                  }

                                </span>


                              </div>


                            </div>


                            <div
                              className={
                                `withdraw-status ${status}`
                              }
                            >

                              <StatusIcon
                                status={
                                  status
                                }
                              />


                              <span>

                                {
                                  status ===
                                  "success"

                                    ? "SUCCESS"

                                    : status ===
                                      "failed"

                                    ? "FAILED"

                                    : "PENDING"
                                }

                              </span>

                            </div>


                            <FaChevronRight
                              className="withdraw-chevron"
                            />


                          </div>

                        );

                      }
                    )}


                  </div>


                </div>

              );

            }
          )}


        </div>

      )}


      {/* =================================================
          FOOTER
      ================================================= */}

      <div className="withdraw-footer">

        <span>
          WITHDRAW ACTIVITY
        </span>


        <small>
          Showing team withdrawals registered today.
        </small>

      </div>


    </div>

  );

};


export default NewWithdraw;
